import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUpRight } from "lucide-react";
import { useHomeTransition, WhiteOut } from "./homeTransition";
import { useContent } from "../lib/content";

export default function Header({ dark = false }) {
  const [aboutOpen, setAboutOpen] = useState(false);
  const navigate = useNavigate();
  const { leaving, goHome } = useHomeTransition();
  const fg = dark ? "text-white" : "text-void";

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-40 px-6 md:px-10 py-5 flex items-center justify-between">
        <button
          data-testid="about-me-btn"
          onClick={() => setAboutOpen(true)}
          className={`font-mono text-[11px] tracking-[0.25em] uppercase ${fg} hover:text-[#FF4500] transition-colors`}
        >
          About Me
        </button>

        <button onClick={goHome} data-testid="logo-home" className="flex items-center leading-none">
          <img src="/logo.png" alt="RE monogram" className={`h-8 w-auto ${dark ? "" : "invert"}`} />
        </button>

        <div className="flex items-center gap-2">
          <Link to="/contact" data-testid="nav-contact" className={`font-mono text-[11px] tracking-[0.25em] uppercase ${fg} hover:text-[#FF4500] transition-colors`}>
            Contact
          </Link>
        </div>
      </header>

      <WhiteOut show={leaving} />

      <AnimatePresence>
        {aboutOpen && <AboutOverlay onClose={() => setAboutOpen(false)} onContact={() => { setAboutOpen(false); navigate("/contact"); }} />}
      </AnimatePresence>
    </>
  );
}

function AboutOverlay({ onClose, onContact }) {
  const content = useContent();
  const paragraphs = (content.about_body || "").split(/\n\s*\n/).filter(Boolean);
  return (
    <motion.div
      data-testid="about-overlay"
      data-lenis-prevent
      className="fixed inset-0 z-[60] bg-white text-void overflow-y-auto"
      style={{ transformOrigin: "top center", perspective: 1600, willChange: "transform, opacity", backfaceVisibility: "hidden" }}
      initial={{ rotateX: -92, opacity: 0.2 }}
      animate={{ rotateX: 0, opacity: 1 }}
      exit={{ rotateX: -92, opacity: 0 }}
      transition={{ duration: 1.05, ease: [0.22, 1, 0.36, 1] }}
    >
      <button data-testid="about-close-btn" onClick={onClose}
        className="fixed top-5 left-1/2 -translate-x-1/2 z-10 w-11 h-11 rounded-full border border-black/15 flex items-center justify-center hover:bg-black hover:text-white transition-colors bg-white/80 backdrop-blur">
        <X size={18} />
      </button>

      <div className="max-w-6xl mx-auto px-6 md:px-12 py-28 md:py-36">
        <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-black/40 mb-8">{content.about_kicker}</p>
        <div className="grid md:grid-cols-[1.4fr_1fr] gap-14 items-start">
          <div>
            <h1 className="font-display font-medium text-5xl sm:text-6xl tracking-tighter leading-[0.95]">
              {content.about_heading}
            </h1>
            {paragraphs.map((p, i) => (
              <p key={i} className={`${i === 0 ? "mt-10" : "mt-6"} text-lg leading-relaxed text-black/70 max-w-xl`}>{p}</p>
            ))}
            <div className="mt-12 flex flex-wrap gap-4">
              <button data-testid="about-contact-btn" onClick={onContact}
                className="inline-flex items-center gap-2 rounded-full border border-black/20 px-6 py-3 text-sm hover:bg-black hover:text-white transition-colors">
                Get in touch <ArrowUpRight size={16} />
              </button>
            </div>
          </div>
          <div className="relative">
            <img src={content.about_portrait}
              alt="Portrait"
              className="w-full aspect-[3/4] object-cover object-[center_25%]" loading="lazy" />
            <div className="mt-6 space-y-3 font-mono text-xs tracking-wide text-black/60">
              <div className="flex justify-between border-b border-black/10 pb-2"><span>ROLE</span><span>{content.about_role}</span></div>
              <div className="flex justify-between border-b border-black/10 pb-2"><span>FOCUS</span><span>{content.about_focus}</span></div>
              <div className="flex justify-between border-b border-black/10 pb-2"><span>CERT</span><span>{content.about_cert}</span></div>
            </div>
            {content.about_photo2 && (
              <img src={content.about_photo2} alt="Second portrait"
                className="mt-8 w-full aspect-[3/4] object-cover" loading="lazy" data-testid="about-photo2" />
            )}
          </div>
        </div>
        <div className="flex justify-center pb-16">
          <button data-testid="about-close-bottom-btn" onClick={onClose}
            className="w-11 h-11 rounded-full border border-black/15 flex items-center justify-center hover:bg-black hover:text-white transition-colors">
            <X size={18} />
          </button>
        </div>
      </div>
    </motion.div>
  );
}
